import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Alert, Card, CardContent, Typography, Stack, Divider, Box, Button, Chip, Tab, Tabs, GlobalStyles } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import TuneIcon from '@mui/icons-material/Tune';
import PrintIcon from '@mui/icons-material/Print';
import HistoryIcon from '@mui/icons-material/HistoryOutlined';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import PageHeader from '@/components/common/PageHeader';
import DataTable from '@/components/common/DataTable';
import type { DataTableColumn } from '@/components/common/DataTable';
import SearchableSelect from '@/components/common/SearchableSelect';
import { GridContainer, GridItem } from '@/components/common/ResponsiveGrid';
import LabelObjectRenderer from '@/components/labelDesigner/LabelObjectRenderer';
import { CANVAS_SCALE } from '@/components/labelDesigner/LabelCanvas';
import { apiClient } from '@/api/axiosClient';
import { applySequenceValue } from '@/lib/autoGenerate';
import { peekSequenceValues, reserveSequenceValues } from '@/lib/sequenceApi';
import { useDateFormat } from '@/hooks/useDateFormat';
import type { LabelObject, LabelTemplate } from '@/types/labelTemplate';
import type { PackageDto, PackingSourceLineDto, PackingTaskDto } from '@/types/outbound';
import type { PackingPrintedLabelDto, PackingPrintHistoryDto, SubmitPackingPrintJobRequest } from '@/types/packingLabeling';

// Smaller than the label designer's own CANVAS_SCALE — this preview shows one tile per package,
// so a slightly more compact scale keeps the grid scannable.
const PREVIEW_SCALE = CANVAS_SCALE * 0.7;

interface PrintJobGroup {
  id: string;
  templateId: string;
  templateName: string;
  printedDate: string;
  printer: string;
  labels: PackingPrintedLabelDto[];
  totalQty: number;
}

export default function PackingLabelPrintPage() {
  const { t } = useTranslation('packingLabeling');
  const { formatDateTime } = useDateFormat();
  const queryClient = useQueryClient();

  const { data: tasks = [] } = useQuery({
    queryKey: ['packing-tasks'],
    queryFn: async () => (await apiClient.get<PackingTaskDto[]>('/packing-tasks')).data,
  });
  const { data: templates = [] } = useQuery({
    queryKey: ['label-templates'],
    queryFn: async () => (await apiClient.get<LabelTemplate[]>('/label-templates')).data,
  });
  const { data: history = [] } = useQuery({
    queryKey: ['packing-print-history'],
    queryFn: async () => (await apiClient.get<PackingPrintHistoryDto[]>('/packing-print-history')).data,
  });

  const [pageTab, setPageTab] = useState<'print' | 'reprint'>('print');
  const [searchParams] = useSearchParams();

  const [taskId, setTaskId] = useState('');
  const [templateId, setTemplateId] = useState('');
  const [printBatch, setPrintBatch] = useState<LabelObject[][] | null>(null);
  const [printBatchPackages, setPrintBatchPackages] = useState<PackageDto[] | null>(null);
  // Captured alongside printBatchPackages — RequestedQty/IssuedQty are line-level (whole-material
  // totals, not per-package), so resolving them per label still needs the source lines to match
  // each package's own MaterialId against (see sampleDataForPackage below).
  const [printBatchLines, setPrintBatchLines] = useState<PackingSourceLineDto[] | null>(null);
  const [printBatchTemplate, setPrintBatchTemplate] = useState<LabelTemplate | null>(null);
  const [printBatchSampleData, setPrintBatchSampleData] = useState<Record<string, string> | null>(null);

  const [reprintTaskId, setReprintTaskId] = useState('');

  // Only a Completed task (its Packages already saved) has anything to print — no Auto/Manual
  // split step here, unlike GRN/Production Label Print: the Packages already ARE the physical
  // split, decided back at Complete Packing time.
  const printableTasks = useMemo(() => tasks.filter((task) => task.status === 'Completed' && task.packages.length > 0), [tasks]);
  const selectedTask = printableTasks.find((task) => task.id === taskId);
  const packages = useMemo(() => selectedTask?.packages ?? [], [selectedTask]);

  // Arriving from the Packing Management table's "Print" action (?taskId=...) pre-selects that
  // task, once it shows up in the printable list.
  useEffect(() => {
    const requestedTaskId = searchParams.get('taskId');
    if (requestedTaskId && !taskId && printableTasks.some((task) => task.id === requestedTaskId)) {
      setTaskId(requestedTaskId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, printableTasks]);

  useEffect(() => {
    setTemplateId('');
  }, [taskId]);

  // Only templates explicitly assigned to Packing are offered here — GRN/Production templates
  // (Generic or Material-scoped) are a separate pool and never leak into this screen, even if
  // Active. A Packing-assigned template is never Material-scoped (see LabelTemplate.AssignmentType),
  // so there's no material match to check.
  const availableTemplates = useMemo(
    () => templates.filter((tpl) => tpl.status === 'Active' && tpl.assignmentType === 'Packing'),
    [templates],
  );
  const selectedTemplate = templates.find((tpl) => tpl.id === templateId);

  // Each auto-generating object gets its own run of values so every preview tile shows the barcode
  // that tile will actually carry. Peeked, never reserved — previewing must not consume barcode
  // numbers, so printing still reserves for real. See applySequenceValue for why the value has to be
  // stamped on with autoGenerate cleared, or the renderer keeps showing the configured start value.
  const autoGenObjects = useMemo(
    () => (selectedTemplate?.objects ?? []).filter(
      (o): o is Extract<LabelObject, { type: 'barcode' | 'qr' | 'text' }> =>
        (o.type === 'barcode' || o.type === 'qr' || o.type === 'text') && !!o.autoGenerate,
    ),
    [selectedTemplate],
  );

  const { data: previewValues } = useQuery({
    queryKey: ['label-sequence-peek', templateId, packages.length, autoGenObjects.map((o) => o.id).join(',')],
    queryFn: async () => {
      const map = new Map<string, string[]>();
      for (const obj of autoGenObjects) {
        map.set(obj.id, await peekSequenceValues(templateId, obj.id, obj.autoGenerate!, packages.length));
      }
      return map;
    },
    enabled: Boolean(templateId) && autoGenObjects.length > 0 && packages.length > 0,
  });

  // Task-level fields — identical across every physical label in the same print job.
  const buildBaseSampleData = useCallback(
    (task: PackingTaskDto | undefined) => ({
      PackingTaskNumber: task?.packingTaskNumber ?? '',
      PickListNumber: task?.pickListNumber ?? '',
      OrderNumber: task?.orderNumber ?? '',
      SourceNumber: task?.orderNumber ?? task?.pickListNumber ?? '',
      Status: task?.status ?? '',
      PrintDate: dayjs().format('MMM D, YYYY'),
      PrintTime: dayjs().format('HH:mm'),
    }),
    [],
  );

  // Per-package fields — a multi-material packing task can have a different material in each
  // package (see PackingPage.tsx's per-material split), so MaterialCode/MaterialName come straight
  // off the package's own denormalized fields rather than a fixed task-level material; RequestedQty/
  // IssuedQty are the whole line's totals for that package's material, found by matching MaterialId.
  const sampleDataForPackage = useCallback(
    (base: Record<string, string>, lines: PackingSourceLineDto[] | undefined, pkg: PackageDto | undefined) => {
      const line = lines?.find((l) => l.materialId === pkg?.materialId);
      return {
        ...base,
        PackageNo: pkg?.packageNo ?? '',
        Qty: pkg ? String(pkg.qty) : '',
        Weight: pkg?.weight != null ? String(pkg.weight) : '',
        Remarks: pkg?.remarks ?? '',
        CarrierName: pkg?.carrierName ?? '',
        MaterialCode: pkg?.materialCode ?? '',
        MaterialName: pkg?.materialName ?? '',
        RequestedQty: line ? String(line.requestedQty) : '',
        IssuedQty: line ? String(line.issuedQty) : '',
      };
    },
    [],
  );

  const sampleData = useMemo(
    () => sampleDataForPackage(buildBaseSampleData(selectedTask), selectedTask?.lines, packages[0]),
    [buildBaseSampleData, sampleDataForPackage, selectedTask, packages],
  );

  const readyToPrint = Boolean(taskId && templateId && packages.length > 0);

  useEffect(() => {
    if (!printBatch) return;
    const handleAfterPrint = () => setPrintBatch(null);
    window.addEventListener('afterprint', handleAfterPrint);
    const raf = requestAnimationFrame(() => requestAnimationFrame(() => window.print()));
    return () => {
      window.removeEventListener('afterprint', handleAfterPrint);
      cancelAnimationFrame(raf);
    };
  }, [printBatch]);

  const printMutation = useMutation({
    mutationFn: async () => {
      if (!selectedTask || !selectedTemplate) throw new Error('Select a packing task and template first.');

      const autoGenObjects = selectedTemplate.objects.filter(
        (o): o is Extract<LabelObject, { type: 'barcode' | 'qr' | 'text' }> => (o.type === 'barcode' || o.type === 'qr' || o.type === 'text') && !!o.autoGenerate,
      );

      const labelCount = packages.length;
      let barcodeValues: string[] | undefined;

      if (autoGenObjects.length === 0) {
        setPrintBatch(Array.from({ length: labelCount }, () => selectedTemplate.objects));
      } else {
        const valuesByObjectId = new Map<string, string[]>();
        for (const obj of autoGenObjects) {
          valuesByObjectId.set(obj.id, await reserveSequenceValues(selectedTemplate.id, obj.id, obj.autoGenerate!, labelCount));
        }
        const instances = Array.from({ length: labelCount }, (_, i) =>
          selectedTemplate.objects.map((o) => {
            if (!valuesByObjectId.has(o.id)) return o;
            const value = valuesByObjectId.get(o.id)![i];
            return applySequenceValue(o, value);
          }),
        );
        setPrintBatch(instances);

        const barcodeObject = autoGenObjects.find((o) => o.type === 'barcode') ?? autoGenObjects.find((o) => o.type === 'qr') ?? autoGenObjects[0];
        barcodeValues = valuesByObjectId.get(barcodeObject.id);
      }

      setPrintBatchPackages(packages);
      setPrintBatchLines(selectedTask.lines);
      setPrintBatchTemplate(selectedTemplate);
      setPrintBatchSampleData(buildBaseSampleData(selectedTask));

      const payload: SubmitPackingPrintJobRequest = { packingTaskId: selectedTask.id, labelTemplateId: selectedTemplate.id, printer: '', barcodeValues };
      await apiClient.post('/packing-print-jobs', payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['packing-print-history'] });
      queryClient.invalidateQueries({ queryKey: ['packing-printed-labels'] });
    },
  });

  // All PackingPrintedLabel rows ever recorded (capped server-side) — used to work out which
  // tasks actually have print history, so the Reprint tab only ever offers something reprintable.
  const { data: allPrintedLabels = [] } = useQuery({
    queryKey: ['packing-printed-labels'],
    queryFn: async () => (await apiClient.get<PackingPrintedLabelDto[]>('/packing-printed-labels')).data,
  });
  const printedPackageIds = useMemo(() => new Set(allPrintedLabels.map((l) => l.packageId)), [allPrintedLabels]);
  const reprintableTasks = useMemo(() => tasks.filter((task) => task.packages.some((p) => printedPackageIds.has(p.id))), [tasks, printedPackageIds]);
  const reprintTask = reprintableTasks.find((task) => task.id === reprintTaskId);

  const printedLabels = useMemo(() => {
    if (!reprintTask) return [];
    const packageIds = new Set(reprintTask.packages.map((p) => p.id));
    return allPrintedLabels.filter((l) => packageIds.has(l.packageId));
  }, [allPrintedLabels, reprintTask]);

  const reprintJobGroups = useMemo<PrintJobGroup[]>(() => {
    const map = new Map<string, PrintJobGroup>();
    for (const label of printedLabels) {
      const existing = map.get(label.packingPrintHistoryEntryId);
      if (existing) {
        existing.labels.push(label);
        existing.totalQty += label.qty;
      } else {
        map.set(label.packingPrintHistoryEntryId, {
          id: label.packingPrintHistoryEntryId,
          templateId: label.labelTemplateId,
          templateName: label.templateName,
          printedDate: label.printedDate,
          printer: label.printer,
          labels: [label],
          totalQty: label.qty,
        });
      }
    }
    return Array.from(map.values()).sort((a, b) => (a.printedDate < b.printedDate ? 1 : -1));
  }, [printedLabels]);

  const reprintMutation = useMutation({
    mutationFn: async (group: PrintJobGroup) => {
      const template = templates.find((tpl) => tpl.id === group.templateId);
      if (!template) throw new Error('The original label template no longer exists.');

      // Re-uses each label's originally recorded BarcodeValue verbatim — never calls
      // reserveSequenceValues here, since a reprint must not advance or duplicate a barcode counter.
      const autoGenObjects = template.objects.filter(
        (o): o is Extract<LabelObject, { type: 'barcode' | 'qr' | 'text' }> => (o.type === 'barcode' || o.type === 'qr' || o.type === 'text') && !!o.autoGenerate,
      );
      const primary = autoGenObjects.find((o) => o.type === 'barcode') ?? autoGenObjects.find((o) => o.type === 'qr') ?? autoGenObjects[0];

      const instances = group.labels.map((label) =>
        template.objects.map((o) => {
          if (!primary || o.id !== primary.id || !label.barcodeValue) return o;
          return applySequenceValue(o, label.barcodeValue);
        }),
      );

      const matchingPackages = group.labels
        .map((label) => reprintTask?.packages.find((p) => p.id === label.packageId))
        .filter((p): p is PackageDto => Boolean(p));

      setPrintBatch(instances);
      setPrintBatchPackages(matchingPackages);
      setPrintBatchLines(reprintTask?.lines ?? null);
      setPrintBatchTemplate(template);
      setPrintBatchSampleData(buildBaseSampleData(reprintTask));

      await apiClient.post(`/packing-print-history/${group.id}/reprint`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['packing-print-history'] });
    },
  });

  const historyColumns: DataTableColumn<PackingPrintHistoryDto>[] = [
    { field: 'dateTime', headerName: t('history.date'), render: (r) => formatDateTime(r.dateTime) },
    { field: 'packingTaskNumber', headerName: t('history.task') },
    { field: 'sourceNumber', headerName: t('history.source'), render: (r) => r.sourceNumber ?? '—' },
    { field: 'template', headerName: t('history.template'), filterable: true },
    { field: 'quantity', headerName: t('history.quantity') },
    { field: 'printer', headerName: t('history.printer'), filterable: true },
    { field: 'user', headerName: t('history.user'), filterable: true },
  ];

  const reprintColumns: DataTableColumn<PrintJobGroup>[] = [
    { field: 'printedDate', headerName: t('history.date'), render: (r) => formatDateTime(r.printedDate) },
    { field: 'templateName', headerName: t('history.template') },
    { field: 'labelCount', headerName: t('reprint.labelCount'), render: (r) => r.labels.length },
    { field: 'totalQty', headerName: t('history.quantity') },
    { field: 'printer', headerName: t('history.printer') },
    {
      field: 'actions',
      headerName: '',
      align: 'right',
      render: (r) => (
        <Button size="small" variant="outlined" startIcon={<PrintIcon fontSize="small" />} onClick={() => reprintMutation.mutate(r)} disabled={reprintMutation.isPending}>
          {t('reprint.reprintButton')}
        </Button>
      ),
    },
  ];

  return (
    <>
      <GlobalStyles
        styles={{
          '@media print': {
            'body *': { visibility: 'hidden' },
            '#packing-label-print-area, #packing-label-print-area *': { visibility: 'visible' },
            '#packing-label-print-area': { position: 'absolute', top: 0, left: 0, margin: 0 },
          },
          '@page': {
            size: printBatchTemplate ? `${printBatchTemplate.widthMm}mm ${printBatchTemplate.heightMm}mm` : 'auto',
            margin: 0,
          },
        }}
      />

      <PageHeader title={t('title')} subtitle={t('subtitle')} />

      <Tabs value={pageTab} onChange={(_, v: 'print' | 'reprint') => setPageTab(v)} sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}>
        <Tab value="print" label={t('tabs.print')} />
        <Tab value="reprint" label={t('tabs.reprint')} />
      </Tabs>

      {pageTab === 'print' && (
        <>
          <Alert severity="info" sx={{ mb: 3 }}>
            {t('completedOnly')}
          </Alert>

          <GridContainer columns={12}>
            <GridItem xs={12} md={4}>
              <Stack spacing={3}>
                <Card>
                  <CardContent>
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
                      <TuneIcon fontSize="small" color="primary" />
                      <Typography variant="h5">{t('selectionCriteria')}</Typography>
                    </Stack>
                    <Stack spacing={2}>
                      <SearchableSelect
                        label={t('selectTask')} fullWidth value={taskId} onChange={setTaskId}
                        options={printableTasks.map((task) => ({ value: task.id, label: `${task.packingTaskNumber} — ${task.orderNumber ?? task.pickListNumber ?? ''}` }))}
                      />
                      <SearchableSelect
                        label={t('selectTemplate')}
                        fullWidth
                        value={templateId}
                        onChange={setTemplateId}
                        disabled={!taskId}
                        helperText={taskId && availableTemplates.length === 0 ? t('noTemplatesAvailable') : undefined}
                        options={availableTemplates.map((tpl) => ({ value: tpl.id, label: tpl.name }))}
                      />
                    </Stack>
                  </CardContent>
                </Card>

                {selectedTask && (
                  <Card>
                    <CardContent>
                      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
                        <InfoOutlinedIcon fontSize="small" color="primary" />
                        <Typography variant="h5">{t('taskDetails')}</Typography>
                      </Stack>
                      <GridContainer columns={12} spacing={2}>
                        <GridItem xs={6}>
                          <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase' }}>
                            {t('taskFields.source')}
                          </Typography>
                          <Typography variant="body2">{selectedTask.orderNumber ?? selectedTask.pickListNumber ?? '—'}</Typography>
                        </GridItem>
                        <GridItem xs={6}>
                          <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase' }}>
                            {t('taskFields.packageCount')}
                          </Typography>
                          <Typography variant="body2">{packages.length}</Typography>
                        </GridItem>
                      </GridContainer>
                    </CardContent>
                  </Card>
                )}

                <Card>
                  <CardContent>
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
                      <PrintIcon fontSize="small" color="primary" />
                      <Typography variant="h5">{t('configuration')}</Typography>
                    </Stack>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      {t('onePerPackageHint', { count: packages.length })}
                    </Typography>
                    <Divider sx={{ mb: 2 }} />
                    <Button fullWidth size="large" variant="contained" startIcon={<PrintIcon />} onClick={() => printMutation.mutate()} disabled={printMutation.isPending || !readyToPrint}>
                      {printMutation.isPending ? t('sendingToPrinter') : t('printLabels', { count: packages.length })}
                    </Button>
                  </CardContent>
                </Card>
              </Stack>
            </GridItem>

            <GridItem xs={12} md={8}>
              <Card sx={{ height: '100%' }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
                  <Typography variant="subtitle2" sx={{ textTransform: 'uppercase' }}>
                    {t('canvasPreviewTitle')}
                  </Typography>
                  <Stack direction="row" spacing={1}>
                    {selectedTemplate && <Chip size="small" label={`${selectedTemplate.widthMm}×${selectedTemplate.heightMm} mm`} />}
                    {selectedTemplate && packages.length > 0 && <Chip size="small" color="primary" variant="filled" label={t('printLabels', { count: packages.length })} />}
                  </Stack>
                </Stack>
                <Box sx={{ minHeight: 480, maxHeight: 640, overflow: 'auto', display: 'flex', flexWrap: 'wrap', alignContent: 'flex-start', gap: 2, bgcolor: 'action.hover', p: 3 }}>
                  {!selectedTemplate ? (
                    <Box sx={{ width: '100%', minHeight: 480, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <Typography variant="body2" color="text.secondary">
                        {t('selectTemplatePrompt')}
                      </Typography>
                    </Box>
                  ) : packages.length === 0 ? (
                    <Box sx={{ width: '100%', minHeight: 480, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <Typography variant="body2" color="text.secondary">
                        {t('noLabelsToPreview')}
                      </Typography>
                    </Box>
                  ) : (
                    packages.map((pkg, i) => (
                      <Stack key={pkg.id} spacing={0.5} alignItems="center">
                        <Box
                          sx={{
                            position: 'relative',
                            width: selectedTemplate.widthMm * PREVIEW_SCALE,
                            height: selectedTemplate.heightMm * PREVIEW_SCALE,
                            bgcolor: '#fff',
                            boxShadow: '0px 4px 14px rgba(0,0,0,0.15)',
                          }}
                        >
                          {selectedTemplate.objects.map((obj) => {
                            // Same substitution the print path performs, so what is previewed is
                            // what prints — one value per package, in package order.
                            const values = previewValues?.get(obj.id);
                            const instance = values?.[i] != null ? applySequenceValue(obj, values[i]) : obj;
                            return (
                              <LabelObjectRenderer key={obj.id} object={instance} sampleData={sampleDataForPackage(sampleData, selectedTask?.lines, pkg)} scale={PREVIEW_SCALE} />
                            );
                          })}
                        </Box>
                        <Typography variant="caption" color="text.secondary">
                          {pkg.packageNo}
                        </Typography>
                      </Stack>
                    ))
                  )}
                </Box>
              </Card>
            </GridItem>
          </GridContainer>

          <Box sx={{ mt: 3 }}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
              <HistoryIcon fontSize="small" color="primary" />
              <Typography variant="h5">{t('history.title')}</Typography>
            </Stack>
            <DataTable columns={historyColumns} rows={history} searchable exportFileName="packing-print-history" />
          </Box>
        </>
      )}

      {pageTab === 'reprint' && (
        <Stack spacing={3}>
          <Card>
            <CardContent>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
                <TuneIcon fontSize="small" color="primary" />
                <Typography variant="h5">{t('reprint.selectionCriteria')}</Typography>
              </Stack>
              <SearchableSelect
                label={t('selectTask')}
                fullWidth
                value={reprintTaskId}
                onChange={setReprintTaskId}
                helperText={reprintableTasks.length === 0 ? t('reprint.noPrintedTasks') : undefined}
                options={reprintableTasks.map((task) => ({ value: task.id, label: `${task.packingTaskNumber} — ${task.orderNumber ?? task.pickListNumber ?? ''}` }))}
              />
            </CardContent>
          </Card>

          {reprintTaskId && (
            <DataTable columns={reprintColumns} rows={reprintJobGroups} searchable={false} exportable={false} emptyMessage={t('reprint.noHistory')} />
          )}
        </Stack>
      )}

      <Box id="packing-label-print-area" sx={{ display: 'none', '@media print': { display: 'block' } }}>
        {printBatch?.map((objects, i) => {
          const labelSampleData = sampleDataForPackage(printBatchSampleData ?? {}, printBatchLines ?? undefined, printBatchPackages?.[i]);
          return (
            <Box
              key={i}
              sx={{
                position: 'relative',
                width: `${printBatchTemplate?.widthMm ?? 100}mm`,
                height: `${printBatchTemplate?.heightMm ?? 60}mm`,
                breakAfter: i === printBatch.length - 1 ? 'auto' : 'page',
                breakInside: 'avoid',
                pageBreakAfter: i === printBatch.length - 1 ? 'auto' : 'always',
                pageBreakInside: 'avoid',
              }}
            >
              {objects.map((obj) => (
                <LabelObjectRenderer key={obj.id} object={obj} sampleData={labelSampleData} scale={3.7795} />
              ))}
            </Box>
          );
        })}
      </Box>
    </>
  );
}
